/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {hideElementById} from '@ion/core/src/utils/BrowserUtils';
import useIsHistoryAvailable from '@ion/dls/src/maps/hooks/useIsHistoryAvailable';
import {dlsColor} from '@ion/dls/src/theme/theme';
import SelectedAssetPopupContent from './maps/SelectedAssetPopupContent';
import {OnFindNearByProps} from './FindNearByPopupContent';
import SelectedAssetPopupCTA from './SelectedAssetPopupCTA';

export interface SelectedAssetPopupProps {
  asset: any;
  mapView: __esri.MapView;
  onCtaClick: (type: CtaType, asset: any) => void;
  onFindNearBySubmit: (props: OnFindNearByProps) => void;
  onPopupClick: (showSidesheet: boolean, defaultAsset?: any, isMapSelection?: boolean) => void;
  onFindNearbyFromItem?: Function;
  onFindNearbyPanelActive?: Function;
  isTrackingV2?: boolean;
}

export enum CtaType {
  HISTORY = 'history',
  FINDNEARBY = 'find nearby',
  STREETVIEW = 'street view',
  NAVIGATION = 'nativate to',
  BACK = 'asset-detail'
};

const getContentContainerHeight = (showFindNearBy) => {
  return showFindNearBy ? 200 : 145;
};

const getCtaContentContainerHeight = (showFindNearBy) => {
  return showFindNearBy ? 200 : 'auto';
};

const getAssetContainerHeight = (showFindNearBy) => {
  return showFindNearBy ? 198 : 125;
};

const useStyles = makeStyles(() => ({
  popupContainer : {
    width        : 295,
    cursor       : 'pointer',
    borderRadius : 4
  },
  tabViewContainer : {
    display      : 'flex',
    height       : '32px',
    width        : '100%',
    borderRadius : '4px 4px 0px 0px'
  },
  contentContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    overflow       : 'hidden',
    justifyContent : 'space-between',
    width          : 295,
    height         : ({showFindNearBy}: any) => getContentContainerHeight(showFindNearBy)
  },
  assetContainer : {
    display : 'flex',
    height  : ({showFindNearBy}: any) => getAssetContainerHeight(showFindNearBy),
    width   : 255
  },
  ctaContainer : {
    display       : 'flex',
    height        : 'auto',
    width         : 37,
    flexDirection : 'column',
    gap           : 2,
    borderRadius  : '4px 4px 0px 0px',
    marginTop     : ({showFindNearBy}: any) => showFindNearBy ? '2px' : 0
  },
  ctaContentContainer : {
    maxWidth        : 37,
    display         : 'flex',
    flexDirection   : 'column',
    alignItems      : 'center',
    justifyContent  : 'space-evenly',
    backgroundColor : dlsColor('grey6'),
    height          : ({showFindNearBy}: any) => getCtaContentContainerHeight(showFindNearBy)
  }
}));

const SelectedAssetPopup = ({
  asset,
  onCtaClick,
  onFindNearBySubmit,
  onPopupClick,
  onFindNearbyFromItem,
  onFindNearbyPanelActive,
  isTrackingV2 = false
}: SelectedAssetPopupProps) => {
  const [showFindNearBy, setShowFindNearby] = useState(false);
  const styles                              = useStyles({showFindNearBy});
  const isHistoryEnabled                    = useIsHistoryAvailable(asset);
  const [showTooltip, setShowTooltip]       = useState(false);

  const setFindNearbyActions = useCallback(() => {
    hideElementById('findNearbyTooltip');
    onFindNearbyFromItem?.(asset);
    onFindNearbyPanelActive?.(true);
  }, [asset]);

  useEffect(function() {
    setShowFindNearby(false);
  }, [asset]);

  const onClickCta= function(cta: CtaType) {
    return (e) => {
      e.stopPropagation();
      e.preventDefault();
      setShowTooltip(false);
      cta !== CtaType.FINDNEARBY && cta !== CtaType.BACK && onCtaClick(cta, asset);
      cta === CtaType.FINDNEARBY && (!isTrackingV2 ? setShowFindNearby(true) : setFindNearbyActions());
      cta === CtaType.BACK && setShowFindNearby(false);
    };
  };

  const cancelFindNearBy = function() {
    setShowFindNearby(false);
  };

  const onContainerClick = useCallback(() => {
    onPopupClick(!showFindNearBy, asset, true);
  }, [showFindNearBy, asset]);

  return (
    <div className={styles.popupContainer}>
      <div className={styles.contentContainer}>
        <div className={styles.assetContainer}
          onClick={onContainerClick}>
          <SelectedAssetPopupContent asset={asset}
            onFindNearBySubmit={onFindNearBySubmit}
            onCancelFindNearBy={cancelFindNearBy}
            showFindNearBy={showFindNearBy} />
        </div>
        <SelectedAssetPopupCTA onClickCta={onClickCta}
          showTooltip={!isTrackingV2 ? showTooltip : undefined}
          setShowTooltip={!isTrackingV2 ? setShowTooltip : undefined}
          isHistoryEnabled={isHistoryEnabled}
          showFindNearBy={showFindNearBy}
          classes={styles} />
      </div>
    </div>
  );
};
export default SelectedAssetPopup;
